import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  User, Palette, Shield, Plus, Trash2, Globe, Eye, EyeOff, Copy, Check, Plug,
} from "lucide-react";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { Button } from "@/components/ui/Button";
import { InputGroup } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/Dialog";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "@/components/ui/Toast";
import { useSettingsStore } from "@/store/settings.store";
import { useLocaleStore } from "@/store/locale.store";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { Theme } from "@/types";

// ─────────────────────────────────────────────
// Settings
//
// Screen — /settings
// Vertical tabs: General, Appearance, Security, Integrations
// Design: §8.5 Settings
// ─────────────────────────────────────────────

type SettingsTab = "general" | "appearance" | "security" | "integrations";

// ── Password strength checker ─────────────────

interface PasswordCriteria {
  label: string;
  met: boolean;
}

function passwordCriteria(pw: string): PasswordCriteria[] {
  return [
    { label: "At least 8 characters", met: pw.length >= 8 },
    { label: "Uppercase letter", met: /[A-Z]/.test(pw) },
    { label: "Lowercase letter", met: /[a-z]/.test(pw) },
    { label: "Number", met: /\d/.test(pw) },
    { label: "Special character (!@#$...)", met: /[^A-Za-z0-9]/.test(pw) },
  ];
}

function PasswordStrength({ password }: { password: string }) {
  const criteria = passwordCriteria(password);
  const metCount = criteria.filter((c) => c.met).length;
  const strength = metCount <= 1 ? "weak" : metCount <= 3 ? "fair" : metCount === 4 ? "good" : "strong";
  const colors = {
    weak: "bg-danger",
    fair: "bg-warning",
    good: "bg-k-orange",
    strong: "bg-success",
  };
  const labels = { weak: "Weak", fair: "Fair", good: "Good", strong: "Strong" };

  if (!password) return null;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className={cn(
                "h-1.5 flex-1 rounded-full transition-all",
                i <= metCount ? colors[strength] : "bg-hover"
              )}
            />
          ))}
        </div>
        <span className={cn("text-caption font-medium", `text-${strength === "weak" ? "danger" : strength === "fair" ? "warning" : strength === "good" ? "k-orange" : "success"}`)}>
          {labels[strength]}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5">
        {criteria.map((c, i) => (
          <li key={i} className={cn("flex items-center gap-1.5 text-caption", c.met ? "text-success" : "text-text-muted")}>
            <span>{c.met ? "✓" : "○"}</span>
            {c.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── API key types ─────────────────────────────

interface ApiKey {
  id: string;
  name: string;
  prefix: string;
  lastUsedAt: string | null;
  createdAt: string;
}

interface Webhook {
  id: string;
  url: string;
  events: string[];
  isActive: boolean;
  createdAt: string;
}

// ── Create API Key Modal ──────────────────────

function CreateApiKeyModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: (n: string) => api.post<{ key: string }>("/api-keys", { name: n }),
    onSuccess: (data) => {
      setCreatedKey(data.key);
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function handleCopy() {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleClose() {
    setName("");
    setCreatedKey(null);
    setCopied(false);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.createApiKey")}</DialogTitle>
        </DialogHeader>
        {!createdKey ? (
          <>
            <div className="py-2">
              <InputGroup
                label={t("settings.keyName")}
                placeholder={t("settings.keyNamePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>{t("common.cancel")}</Button>
              <Button onClick={() => mutation.mutate(name)} isLoading={mutation.isPending} disabled={!name.trim()}>
                {t("settings.createKey")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3 py-2">
              <div className="rounded-card border border-warning/30 bg-warning-bg p-3">
                <p className="text-[13px] font-medium text-warning">
                  {t("settings.copyKey")}
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-control border border-border-default bg-bg-input px-3 py-2">
                <span className="flex-1 truncate font-mono text-[13px] text-text-primary">{createdKey}</span>
                <button type="button" onClick={handleCopy} className="flex-shrink-0 text-text-muted hover:text-text-primary">
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{t("common.done")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Add Webhook Modal ─────────────────────────

const WEBHOOK_EVENTS = [
  "assessment.submitted", "assessment.approved", "assessment.rejected",
  "vendor_assessment.completed", "entity.created", "user.created",
];

function AddWebhookModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  // The signing secret is disclosed exactly once by the API on creation —
  // show it (same show-once pattern as API keys) or HMAC verification is
  // impossible for the receiver.
  const [createdSecret, setCreatedSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () =>
      api.post<{ data: { signingSecret: string } }>("/webhooks", { url, events }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      toast.success(t("settings.webhookAdded"));
      setCreatedSecret(res.data.signingSecret);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  function toggleEvent(evt: string) {
    setEvents((prev) => prev.includes(evt) ? prev.filter((e) => e !== evt) : [...prev, evt]);
  }

  function handleCopy() {
    if (createdSecret) {
      navigator.clipboard.writeText(createdSecret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleClose() {
    setUrl("");
    setEvents([]);
    setCreatedSecret(null);
    setCopied(false);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.addWebhook")}</DialogTitle>
        </DialogHeader>
        {!createdSecret ? (
          <>
            <div className="flex flex-col gap-4 py-2">
              <InputGroup
                label={t("settings.endpointUrl")}
                placeholder={t("settings.endpointPlaceholder")}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                type="url"
              />
              <div>
                <p className="mb-2 text-[14px] font-medium text-text-secondary">{t("settings.events")}</p>
                <div className="flex flex-col gap-2">
                  {WEBHOOK_EVENTS.map((evt) => (
                    <label key={evt} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={events.includes(evt)}
                        onChange={() => toggleEvent(evt)}
                        className="rounded border-border-default bg-bg-input text-accent focus:ring-accent"
                      />
                      <span className="text-body text-text-secondary font-mono text-[13px]">{evt}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={handleClose}>{t("common.cancel")}</Button>
              <Button
                onClick={() => mutation.mutate()}
                isLoading={mutation.isPending}
                disabled={!url.trim() || events.length === 0}
              >
                {t("settings.addWebhook")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3 py-2">
              <div className="rounded-card border border-warning/30 bg-warning-bg p-3">
                <p className="text-[13px] font-medium text-warning">
                  {t("settings.copyWebhookSecret")}
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-control border border-border-default bg-bg-input px-3 py-2">
                <span className="flex-1 truncate font-mono text-[13px] text-text-primary">{createdSecret}</span>
                <button type="button" onClick={handleCopy} className="flex-shrink-0 text-text-muted hover:text-text-primary">
                  {copied ? <Check className="h-4 w-4 text-success" /> : <Copy className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>{t("common.done")}</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Tab panels ────────────────────────────────

function GeneralTab() {
  const { user, setUser } = useAuthStore();
  const { t } = useTranslation();
  const [name, setName] = useState(user?.name ?? "");

  const mutation = useMutation({
    mutationFn: (newName: string) =>
      api.put<{ data: { id: string; name: string; email: string } }>("/users/me", { name: newName }),
    onSuccess: (res) => {
      const saved = res.data.name;
      if (user) setUser({ ...user, name: saved });
      setName(saved);
      toast.success(t("settings.profileSaved"));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const trimmedName = name.trim();
  const isDirty = trimmedName !== (user?.name ?? "");

  return (
    <div className="flex flex-col gap-6">
      <div className="rounded-card border border-border-default bg-card p-6">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("settings.profile")}</h3>
        <div className="flex flex-col gap-4">
          <InputGroup
            label={t("settings.fullName")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <InputGroup
            label={t("settings.email")}
            defaultValue={user?.email ?? ""}
            disabled
            helperText={t("settings.emailReadOnly")}
          />
          <div>
            <p className="mb-1 text-[13px] text-text-secondary">{t("settings.role")}</p>
            <Badge variant="secondary">{user?.role ?? "-"}</Badge>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button
          variant="ghost"
          onClick={() => setName(user?.name ?? "")}
          disabled={!isDirty || mutation.isPending}
        >
          {t("common.reset")}
        </Button>
        <Button
          onClick={() => mutation.mutate(trimmedName)}
          isLoading={mutation.isPending}
          disabled={!isDirty || !trimmedName}
        >
          {t("common.save")}
        </Button>
      </div>
    </div>
  );
}

function AppearanceTab() {
  const { theme, setTheme } = useSettingsStore();
  const { language, setLanguage } = useLocaleStore();
  const { t } = useTranslation();

  const themes: { value: Theme; label: string; desc: string }[] = [
    { value: "dark", label: t("settings.dark"), desc: t("settings.darkThemeDesc") },
    { value: "system", label: t("settings.system"), desc: t("settings.systemThemeDesc") },
    { value: "light", label: t("settings.light"), desc: t("settings.lightThemeDesc") },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Theme */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("settings.themeMode")}</h3>
        <div className="grid grid-cols-3 gap-3">
          {themes.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTheme(t.value)}
              className={cn(
                "flex flex-col gap-1 rounded-card border p-4 text-left transition-colors",
                theme === t.value
                  ? "border-accent bg-accent-bg"
                  : "border-border-default bg-hover hover:border-accent/50"
              )}
            >
              <span className={cn("text-[14px] font-medium", theme === t.value ? "text-accent-text" : "text-text-primary")}>
                {t.label}
              </span>
              <span className="text-caption text-text-muted">{t.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Language */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("settings.language")}</h3>
        <div className="flex gap-3">
          {([{ value: "en", label: "English" }, { value: "sr", label: "Srpski" }] as const).map((lang) => (
            <button
              key={lang.value}
              type="button"
              onClick={() => setLanguage(lang.value)}
              className={cn(
                "flex items-center gap-2 rounded-card border px-4 py-2 text-[14px] transition-colors",
                language === lang.value
                  ? "border-accent bg-accent-bg text-accent-text"
                  : "border-border-default bg-hover text-text-secondary hover:border-accent/50"
              )}
            >
              <Globe className="h-4 w-4" />
              {lang.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function SecurityTab() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwError, setPwError] = useState("");

  const mutation = useMutation({
    mutationFn: async (body: { currentPassword: string; newPassword: string }) => {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error((data as { message?: string }).message ?? "Failed to change password");
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success(t("settings.passwordChanged"));
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
      setPwError("");
    },
    onError: (err: Error) => {
      setPwError(err.message ?? "Failed to change password");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPwError("");
    if (newPw !== confirmPw) {
      setPwError(t("settings.passwordMismatch"));
      return;
    }
    const criteria = passwordCriteria(newPw);
    const metCount = criteria.filter((c) => c.met).length;
    if (metCount < 3) {
      setPwError(t("settings.passwordWeak"));
      return;
    }
    mutation.mutate({ currentPassword: currentPw, newPassword: newPw });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Change password */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("settings.changePassword")}</h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-md">
          <InputGroup
            label={t("settings.currentPassword")}
            type={showCurrent ? "text" : "password"}
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            required
            trailingIcon={
              <button type="button" onClick={() => setShowCurrent((v) => !v)} className="text-text-muted hover:text-text-primary">
                {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />
          <InputGroup
            label={t("settings.newPassword")}
            type={showNew ? "text" : "password"}
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            required
            trailingIcon={
              <button type="button" onClick={() => setShowNew((v) => !v)} className="text-text-muted hover:text-text-primary">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            }
          />
          {newPw && <PasswordStrength password={newPw} />}
          <InputGroup
            label={t("settings.confirmPassword")}
            type="password"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            required
            errorText={pwError}
          />
          <div className="flex justify-end">
            <Button type="submit" isLoading={mutation.isPending}>{t("settings.changePassword")}</Button>
          </div>
        </form>
      </div>

      {/* Session info */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <h3 className="mb-4 text-[16px] font-semibold text-text-primary">{t("settings.session")}</h3>
        <dl className="flex flex-col gap-3">
          <div>
            <dt className="text-caption text-text-muted">{t("settings.signedInAs")}</dt>
            <dd className="mt-0.5 text-body text-text-primary">{user?.email ?? "-"}</dd>
          </div>
          <div>
            <dt className="text-caption text-text-muted">{t("settings.organization")}</dt>
            <dd className="mt-0.5 text-body text-text-secondary">{user?.organizationId ?? "-"}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// Integrations tab deferred — see ITEM-002
function IntegrationsTab() {
  const { user } = useAuthStore();
  const { t, language } = useTranslation();
  const queryClient = useQueryClient();
  // Role name comes from UserProfile.role.name — stored as-is (e.g., "Org Admin")
  const canManageIntegrations = userCan(user, "read", "ApiKey");
  const [apiKeyOpen, setApiKeyOpen] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [deleteApiKeyId, setDeleteApiKeyId] = useState<string | null>(null);
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null);

  const { data: apiKeysData, isLoading: keysLoading } = useQuery<{ data: ApiKey[] }>({
    queryKey: ["api-keys"],
    queryFn: () => api.get("/api-keys"),
    enabled: canManageIntegrations,
  });

  const { data: webhooksData, isLoading: hooksLoading } = useQuery<{ data: Webhook[] }>({
    queryKey: ["webhooks"],
    queryFn: () => api.get("/webhooks"),
    enabled: canManageIntegrations,
  });

  const deleteKeyMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api-keys/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["api-keys"] });
      toast.success(t("settings.apiKeyDeleted"));
      setDeleteApiKeyId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/webhooks/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["webhooks"] });
      toast.success(t("settings.webhookDeleted"));
      setDeleteWebhookId(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!canManageIntegrations) {
    return (
      <div className="rounded-card border border-border-default bg-card p-6">
        <p className="text-body text-text-muted">
          {t("settings.integrationsAdminOnly")}
        </p>
      </div>
    );
  }

  const apiKeys = apiKeysData?.data ?? [];
  const webhooks = webhooksData?.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      {/* API Keys */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-text-primary">{t("settings.apiKeys")}</h3>
          <Button size="sm" onClick={() => setApiKeyOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t("settings.createKey")}
          </Button>
        </div>
        {keysLoading ? (
          <p className="text-body text-text-muted">Loading…</p>
        ) : apiKeys.length === 0 ? (
          <p className="text-body text-text-muted">{t("settings.noApiKeys")}</p>
        ) : (
          <table className="w-full text-body">
            <thead>
              <tr className="border-b border-border-default">
                <th className="pb-2 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">{t("settings.keyName")}</th>
                <th className="pb-2 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">{t("settings.prefix")}</th>
                <th className="pb-2 text-left text-table-header font-medium uppercase tracking-table-header text-text-muted">{t("settings.lastUsed")}</th>
                <th className="w-8 pb-2" />
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((k) => (
                <tr key={k.id} className="border-b border-border-subtle last:border-0">
                  <td className="py-3 text-text-primary">{k.name}</td>
                  <td className="py-3 font-mono text-[13px] text-text-muted">{k.prefix}…</td>
                  <td className="py-3 text-text-secondary">
                    {k.lastUsedAt ? formatDate(k.lastUsedAt, language) : t("settings.never")}
                  </td>
                  <td className="py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setDeleteApiKeyId(k.id)}
                      className="text-text-muted hover:text-danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Webhooks */}
      <div className="rounded-card border border-border-default bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-[16px] font-semibold text-text-primary">{t("settings.webhooks")}</h3>
          <Button size="sm" onClick={() => setWebhookOpen(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {t("settings.addWebhook")}
          </Button>
        </div>
        <p className="mb-3 text-caption text-text-muted">{t("settings.webhookDispatchNote")}</p>
        {hooksLoading ? (
          <p className="text-body text-text-muted">Loading…</p>
        ) : webhooks.length === 0 ? (
          <p className="text-body text-text-muted">{t("settings.noWebhooks")}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {webhooks.map((wh) => (
              <div key={wh.id} className="flex items-start justify-between gap-3 rounded-control border border-border-default bg-hover p-3">
                <div>
                  <p className="font-mono text-[13px] text-text-primary">{wh.url}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {wh.events.map((e) => (
                      <span key={e} className="rounded-badge bg-accent-bg px-2 py-0.5 font-mono text-[11px] text-accent-text">
                        {e}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={cn("text-caption", wh.isActive ? "text-success" : "text-text-muted")}>
                    {wh.isActive ? t("status.active") : t("status.inactive")}
                  </span>
                  <button
                    type="button"
                    onClick={() => setDeleteWebhookId(wh.id)}
                    className="text-text-muted hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateApiKeyModal open={apiKeyOpen} onClose={() => setApiKeyOpen(false)} />
      <AddWebhookModal open={webhookOpen} onClose={() => setWebhookOpen(false)} />

      <ConfirmDialog
        open={Boolean(deleteApiKeyId)}
        onOpenChange={(v) => !v && setDeleteApiKeyId(null)}
        title={t("settings.deleteKey")}
        description={t("settings.deleteKeyDesc")}
        confirmLabel={t("settings.deleteConfirmKey")}
        isDestructive
        onConfirm={() => deleteApiKeyId && deleteKeyMutation.mutate(deleteApiKeyId)}
        isLoading={deleteKeyMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(deleteWebhookId)}
        onOpenChange={(v) => !v && setDeleteWebhookId(null)}
        title={t("settings.deleteWebhook")}
        description={t("settings.deleteWebhookDesc")}
        confirmLabel={t("settings.deleteWebhookConfirm")}
        isDestructive
        onConfirm={() => deleteWebhookId && deleteWebhookMutation.mutate(deleteWebhookId)}
        isLoading={deleteWebhookMutation.isPending}
      />
    </div>
  );
}

// ── Main Settings page ────────────────────────

export function Settings() {
  const [tab, setTab] = useState<SettingsTab>("general");
  const { t } = useTranslation();

  const TABS: { value: SettingsTab; label: string; icon: typeof User }[] = [
    { value: "general", label: t("settings.general"), icon: User },
    { value: "appearance", label: t("settings.appearance"), icon: Palette },
    { value: "security", label: t("settings.security"), icon: Shield },
    // S-168: re-enabled — the missing /api-keys CRUD now exists
    { value: "integrations", label: t("settings.integrations"), icon: Plug },
  ];

  return (
    <>
      {/* Page header */}
      <div>
        <h1 className="text-[24px] font-semibold text-text-primary">{t("settings.title")}</h1>
        <p className="mt-1 text-body text-text-muted">{t("settings.subtitle")}</p>
      </div>

      <div className="flex gap-6">
        {/* Vertical tab nav */}
        <div className="w-52 flex-shrink-0">
          <nav className="flex flex-col gap-0.5">
            {TABS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => setTab(value)}
                className={cn(
                  "flex items-center gap-3 rounded-control px-3 py-2.5 text-left text-body transition-colors",
                  tab === value
                    ? "bg-accent-bg text-accent-text"
                    : "text-text-secondary hover:bg-hover hover:text-text-primary"
                )}
              >
                <Icon className={cn("h-4 w-4", tab === value ? "text-accent" : "text-text-muted")} />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {tab === "general" && <GeneralTab />}
          {tab === "appearance" && <AppearanceTab />}
          {tab === "security" && <SecurityTab />}
          {tab === "integrations" && <IntegrationsTab />}
        </div>
      </div>
    </>
  );
}
